/**
 * PlayerNameModal - Modal component for entering/editing player name
 */

import type { PlayerService } from '@/player/PlayerService';

export interface PlayerNameModalOptions {
  onNameSet?: (name: string) => void;
}

export class PlayerNameModal {
  private container: HTMLElement;
  private element: HTMLElement | null = null;
  private playerService: PlayerService;
  private onNameSet: (name: string) => void;
  private allowCancel: boolean = false;

  constructor(
    container: HTMLElement,
    playerService: PlayerService,
    options: PlayerNameModalOptions = {}
  ) {
    this.container = container;
    this.playerService = playerService;
    this.onNameSet = options.onNameSet || (() => {});
    this.createElement();
    this.hide();
  }

  private createElement(): void {
    this.element = document.createElement('div');
    this.element.id = 'player-name-modal';
    this.element.innerHTML = `
      <style>
        #player-name-modal {
          position: fixed;
          inset: 0;
          background: rgba(10, 12, 18, 0.95);
          z-index: 10000;
          font-family: system-ui, -apple-system, sans-serif;
          color: white;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        .pnm-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          text-align: center;
          padding: 32px;
          background: rgba(30, 35, 45, 0.9);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
          max-width: 400px;
          width: 90%;
        }

        .pnm-title {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 8px;
        }

        .pnm-subtitle {
          font-size: 14px;
          opacity: 0.7;
          margin-bottom: 8px;
        }

        .pnm-input-container {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .pnm-input {
          width: 100%;
          padding: 14px 16px;
          font-size: 18px;
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 10px;
          background: rgba(0, 0, 0, 0.3);
          color: white;
          text-align: center;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .pnm-input:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .pnm-input.error {
          border-color: #ef4444;
        }

        .pnm-input::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .pnm-counter {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          text-align: right;
        }

        .pnm-counter.warning {
          color: #fbbf24;
        }

        .pnm-counter.error {
          color: #ef4444;
        }

        .pnm-error {
          font-size: 13px;
          color: #ef4444;
          min-height: 20px;
        }

        .pnm-buttons {
          display: flex;
          gap: 12px;
          width: 100%;
          margin-top: 8px;
        }

        .pnm-btn {
          flex: 1;
          padding: 14px 24px;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s, transform 0.1s;
        }

        .pnm-btn:hover:not(:disabled) {
          transform: scale(1.02);
        }

        .pnm-btn:active:not(:disabled) {
          transform: scale(0.98);
        }

        .pnm-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pnm-btn--primary {
          background: #22c55e;
          color: white;
          box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
        }

        .pnm-btn--primary:hover:not(:disabled) {
          background: #16a34a;
        }

        .pnm-btn--cancel {
          background: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.8);
        }

        .pnm-btn--cancel:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.15);
        }

        .hidden {
          display: none !important;
        }
      </style>

      <div class="pnm-content">
        <div class="pnm-title">Enter Your Name</div>
        <div class="pnm-subtitle">Choose a name for battles (2-16 characters)</div>

        <div class="pnm-input-container">
          <input
            type="text"
            class="pnm-input"
            id="pnm-input"
            placeholder="Your name"
            maxlength="16"
            autocomplete="off"
          />
          <div class="pnm-counter" id="pnm-counter">0 / 16</div>
        </div>

        <div class="pnm-error" id="pnm-error"></div>

        <div class="pnm-buttons">
          <button class="pnm-btn pnm-btn--cancel hidden" id="pnm-cancel-btn">Cancel</button>
          <button class="pnm-btn pnm-btn--primary" id="pnm-submit-btn" disabled>Confirm</button>
        </div>
      </div>
    `;

    this.container.appendChild(this.element);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    const input = document.getElementById('pnm-input') as HTMLInputElement;
    const submitBtn = document.getElementById('pnm-submit-btn') as HTMLButtonElement;
    const cancelBtn = document.getElementById('pnm-cancel-btn') as HTMLButtonElement;
    const counter = document.getElementById('pnm-counter');
    const errorEl = document.getElementById('pnm-error');

    input?.addEventListener('input', () => {
      const value = input.value;
      const len = value.trim().length;

      // Update counter
      if (counter) {
        counter.textContent = `${value.length} / 16`;
        counter.className = 'pnm-counter';
        if (len > 14) {
          counter.classList.add('warning');
        }
        if (value.length >= 16) {
          counter.classList.add('error');
        }
      }

      // Validate and update UI
      const validation = this.playerService.validateName(value);
      if (errorEl) {
        errorEl.textContent = value.length > 0 && !validation.success ? (validation.error || '') : '';
      }
      input.classList.toggle('error', value.length > 0 && !validation.success);
      submitBtn.disabled = !validation.success;
    });

    input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !submitBtn.disabled) {
        this.submit();
      }
      if (e.key === 'Escape' && this.allowCancel) {
        this.hide();
      }
    });

    submitBtn?.addEventListener('click', () => {
      this.submit();
    });

    cancelBtn?.addEventListener('click', () => {
      this.hide();
    });
  }

  private submit(): void {
    const input = document.getElementById('pnm-input') as HTMLInputElement;
    const errorEl = document.getElementById('pnm-error');

    if (!input) return;

    const result = this.playerService.setName(input.value);

    if (result.success) {
      this.hide();
      this.onNameSet(input.value.trim());
    } else if (errorEl) {
      errorEl.textContent = result.error || 'Invalid name';
    }
  }

  /**
   * Show the modal
   * @param allowCancel Whether to show the cancel button
   */
  show(allowCancel: boolean = false): void {
    if (!this.element) return;

    this.allowCancel = allowCancel;
    const cancelBtn = document.getElementById('pnm-cancel-btn');
    const input = document.getElementById('pnm-input') as HTMLInputElement;
    const submitBtn = document.getElementById('pnm-submit-btn') as HTMLButtonElement;
    const counter = document.getElementById('pnm-counter');
    const errorEl = document.getElementById('pnm-error');

    // Show/hide cancel button
    if (cancelBtn) {
      cancelBtn.classList.toggle('hidden', !allowCancel);
    }

    // Pre-fill with current name if editing
    if (input && allowCancel && this.playerService.hasName()) {
      input.value = this.playerService.getName();
      if (counter) {
        counter.textContent = `${input.value.length} / 16`;
      }
      const validation = this.playerService.validateName(input.value);
      submitBtn.disabled = !validation.success;
    } else if (input) {
      input.value = '';
      if (counter) {
        counter.textContent = '0 / 16';
      }
      submitBtn.disabled = true;
    }

    // Clear error
    if (errorEl) {
      errorEl.textContent = '';
    }

    this.element.style.display = 'flex';

    // Focus input after showing
    setTimeout(() => input?.focus(), 50);
  }

  /**
   * Hide the modal
   */
  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  /**
   * Check if modal is visible
   */
  isVisible(): boolean {
    return this.element?.style.display !== 'none';
  }

  /**
   * Clean up
   */
  dispose(): void {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
